import React, { useEffect, useState, useRef } from 'react';
import 'aframe';
import { Card, IconButton, Box, CircularProgress, Tooltip } from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import StreetviewIcon from '@mui/icons-material/Streetview';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';

import videoBackgroundDefault from '../../assets/default-video-background.jpg';
import './styles.css';
import { getSignedURLs } from '../../utils/helpers';

let mapsApiPromise = null;
const loadMapsApi = () => {
	if (!mapsApiPromise) {
		mapsApiPromise = new Promise((resolve, reject) => {
			if (window.google?.maps) { resolve(); return; }
			const script = document.createElement('script');
			script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}`;
			script.async = true;
			script.onload = resolve;
			script.onerror = reject;
			document.head.appendChild(script);
		});
	}
	return mapsApiPromise;
};

const MediaDisplay = ({
	nextImage,
	prevImage,
	images,
	loading,
	currentImage,
	orgId,
	projectId,
	projectName,
	imageIndexes: { fromIndex, toIndex },
	currentCoordinate,
}) => {
	const [imageLoadStatus, setImageLoadStatus] = useState({});
	const [currentImageUrl, setCurrentImageUrl] = useState(null);
	const [visibleImageUrls, setVisibleImageUrls] = useState({});
	const [cameraFov, setCameraFov] = useState(80);
	const [isFullscreen, setIsFullscreen] = useState(false);
	const cameraRef = useRef(null);
	const sceneRef = useRef(null);
	const cardRef = useRef(null);
	const [showStreetView, setShowStreetView] = useState(false);
	const streetViewRef = useRef(null);
	const panoramaRef = useRef(null);

	const handleFullscreen = () => {
		if (!isFullscreen) {
			if (cardRef.current?.requestFullscreen) cardRef.current.requestFullscreen();
		} else {
			if (document.exitFullscreen) document.exitFullscreen();
		}
	};

	useEffect(() => {
		const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
		document.addEventListener('fullscreenchange', onFsChange);
		return () => document.removeEventListener('fullscreenchange', onFsChange);
	}, []);

	useEffect(() => {
		if (showStreetView) {
			setShowStreetView(false);
		}
	}, [currentImage]);

	useEffect(() => {
		if (!showStreetView || !currentCoordinate) return;
		loadMapsApi().then(() => {
			if (!streetViewRef.current) return;
			const position = { lat: currentCoordinate.latitude, lng: currentCoordinate.longitude };
			if (panoramaRef.current) {
				panoramaRef.current.setPosition(position);
			} else {
				panoramaRef.current = new window.google.maps.StreetViewPanorama(streetViewRef.current, {
					position,
					pov: { heading: 0, pitch: 0 },
					zoom: 1,
				});
			}
		});
	}, [showStreetView, currentCoordinate]);

	// Load signed URLs for visible images (for A-Frame assets)
	useEffect(() => {
		if (!projectId || images.length === 0) {
			setVisibleImageUrls({});
			return;
		}

		const loadVisibleUrls = async () => {
			try {
				const visibleImages = images.slice(fromIndex, toIndex);
				
				// Ensure current image is always included
				const currentImageIndex = images.findIndex(img => img.fileName === currentImage?.fileName);
				if (currentImage && currentImageIndex !== -1 && (currentImageIndex < fromIndex || currentImageIndex >= toIndex)) {
					visibleImages.push(currentImage);
				}
				
				const keys = visibleImages.map(img => `${projectId}/images/${img.fileName}`);
				const urlMap = await getSignedURLs(projectId, keys);
				setVisibleImageUrls(urlMap);
			} catch (error) {
				console.error('Error loading signed URLs for visible images:', error);
				setVisibleImageUrls({});
			}
		};

		loadVisibleUrls();
	}, [projectId, images, fromIndex, toIndex, currentImage]);

	// Load signed URL for current image
	useEffect(() => {
		if (!currentImage || !projectId) {
			setCurrentImageUrl(null);
			return;
		}

		const loadImageUrl = async () => {
			try {
				const key = `${projectId}/images/${currentImage.fileName}`;
				const urlMap = await getSignedURLs(projectId, [key]);
				const loadedUrl = urlMap[key] || null;
				setCurrentImageUrl(loadedUrl);
				
				// Mark as loaded once URL is fetched
				if (loadedUrl) {
					setImageLoadStatus((prev) => ({
						...prev,
						[currentImage.fileName]: true,
					}));
				}
			} catch (error) {
				console.error('Error loading signed URL:', error);
				setCurrentImageUrl(null);
			}
		};

		// Reset load status when image changes
		setImageLoadStatus((prev) => ({
			...prev,
			[currentImage.fileName]: false,
		}));
		
		loadImageUrl();
	}, [currentImage, projectId]);

	useEffect(() => {
		const loadStatus = images.slice(fromIndex, toIndex).reduce(
			(acc, image) => ({
				...acc,
				[image.fileName]: imageLoadStatus[image.fileName] || false,
			}),
			{}
		);
		setImageLoadStatus(loadStatus);
	}, [images, fromIndex, toIndex]);

	const handleImageLoad = (fileName) => {
		if (fileName in imageLoadStatus) {
			setImageLoadStatus((prev) => ({
				...prev,
				[fileName]: true,
			}));
		}
	};

	// Only show sky when current image URL is loaded AND exists in visible images
	const currentImageKey = currentImage ? `${projectId}/images/${currentImage.fileName}` : null;
	const currentImageLoaded = currentImageKey && visibleImageUrls[currentImageKey];
	
	const isImageLoading = loading || !currentImageUrl || !currentImageLoaded;

	// Apply FOV changes to A-Frame camera
	useEffect(() => {
		const cameraEl = cameraRef.current;
		if (!cameraEl) return;
		cameraEl.setAttribute('camera', 'fov', cameraFov);
		const threeCamera = cameraEl.getObject3D('camera');
		if (threeCamera) {
			threeCamera.fov = cameraFov;
			threeCamera.updateProjectionMatrix();
		}
	}, [cameraFov]);

	// Wheel / pinch to zoom
	useEffect(() => {
		const sceneEl = sceneRef.current;
		if (!sceneEl) return;

		const setupListeners = () => {
			const canvasEl = sceneEl.canvas || sceneEl; // fallback
			if (!canvasEl) return;

			const clamp = (val, min, max) => Math.min(max, Math.max(min, val));
			const handleWheel = (e) => {
				// Allow pinch-zoom on mac trackpads (comes as wheel with ctrlKey)
				// Prevent page zoom/scroll while interacting with viewer
				e.preventDefault();
				if (isImageLoading) return;
				const step = 2;
				const delta = e.deltaY > 0 ? step : -step; // down -> zoom out (increase FOV)
				setCameraFov((prev) => clamp(prev + delta, 20, 100));
			};

			const handleGesture = (e) => {
				// Safari gesture events (scale > 1 => zoom in)
				e.preventDefault();
				if (isImageLoading) return;
				const step = 2;
				const delta = e.scale > 1 ? -step : step; // zoom in decreases FOV
				setCameraFov((prev) => clamp(prev + delta, 20, 100));
			};

			canvasEl.addEventListener('wheel', handleWheel, { passive: false });
			canvasEl.addEventListener('gesturechange', handleGesture, { passive: false });

			return () => {
				canvasEl.removeEventListener('wheel', handleWheel);
				canvasEl.removeEventListener('gesturechange', handleGesture);
			};
		};

		if (!sceneEl.hasLoaded) {
			const onLoaded = () => {
				const cleanup = setupListeners();
				// store cleanup on element to remove later
				sceneEl.__zoomCleanup = cleanup;
			};
			sceneEl.addEventListener('loaded', onLoaded, { once: true });
			return () => {
				sceneEl.removeEventListener('loaded', onLoaded);
				if (sceneEl.__zoomCleanup) sceneEl.__zoomCleanup();
			};
		}

		const cleanup = setupListeners();
		return cleanup;
	}, [isImageLoading]);

	useEffect(() => {
		const handleKeyDown = (event) => {
			if (!isImageLoading) {
				if (event.key === 'ArrowLeft' && currentImage.fileName !== images[0].fileName) {
					prevImage();
				}
				if (event.key === 'ArrowRight' && currentImage.fileName !== images[images.length - 1].fileName) {
					nextImage();
				}
			}
		};
		if (images.length !== 0) {
			window.addEventListener('keydown', handleKeyDown);
		}

		return () => {
			if (images.length !== 0) {
				window.removeEventListener('keydown', handleKeyDown);
			}
		};
	}, [isImageLoading, currentImage, images, prevImage, nextImage]);

	return (
		<div className="viewer">
			{images.length !== 0 && currentImage ? (
				<>
					<Card ref={cardRef} style={{ width: '100%', height: '100%', position: 'relative', borderRadius: 0 }}>
						{isImageLoading && (
							<Box
								display="flex"
								justifyContent="center"
								alignItems="center"
								style={{ width: '100%', height: '100%', backgroundColor: '#656565' }}
							>
								<CircularProgress size={50} />
							</Box>
						)}
						{/* Navigation Arrows */}
						<Box
						className="viewer__nav-zone"
						role="button"
						tabIndex={isImageLoading || !currentImage || currentImage.fileName === images[0]?.fileName ? -1 : 0}
						onClick={!isImageLoading && currentImage && currentImage.fileName !== images[0]?.fileName ? prevImage : undefined}
						onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !isImageLoading && currentImage && currentImage.fileName !== images[0]?.fileName) prevImage(); }}
						sx={{
							position: 'absolute',
							left: 0,
							top: 0,
							bottom: 0,
							width: '9%',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							cursor: isImageLoading || !currentImage || currentImage.fileName === images[0]?.fileName ? 'default' : 'pointer',
							zIndex: 1000,
							transition: 'background 0.2s ease',
							'&:hover': {
								backgroundImage: isImageLoading || !currentImage || currentImage.fileName === images[0]?.fileName ? 'none' : 'linear-gradient(to right, rgba(0,0,0,0.48), transparent)',
								'& .nav-arrow': {
									opacity: isImageLoading || !currentImage || currentImage.fileName === images[0]?.fileName ? 0.22 : 1,
									transform: 'scale(1.12)',
								},
								'& .nav-arrow-shell': {
									background: isImageLoading || !currentImage || currentImage.fileName === images[0]?.fileName ? 'transparent' : 'rgba(0,0,0,0.30)',
								},
							},
							'&:active': {
								'& .nav-arrow-shell': { background: 'rgba(0,0,0,0.46)' },
							},
						}}
					>
						<span className="nav-arrow-shell">
						<ChevronLeftIcon
							className="nav-arrow"
							sx={{
								color: '#fff',
								fontSize: '2rem',
									filter: 'drop-shadow(0 1px 5px rgba(0,0,0,0.8))',
									transition: 'opacity 0.2s ease, transform 0.2s ease',
								}}
							/>
						</span>
					</Box>

					<Box
						className="viewer__nav-zone"
						role="button"
						tabIndex={isImageLoading || !currentImage || currentImage.fileName === images[images.length - 1]?.fileName ? -1 : 0}
						onClick={!isImageLoading && currentImage && currentImage.fileName !== images[images.length - 1]?.fileName ? nextImage : undefined}
						onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !isImageLoading && currentImage && currentImage.fileName !== images[images.length - 1]?.fileName) nextImage(); }}
						sx={{
							position: 'absolute',
							right: 0,
							top: 0,
							bottom: 0,
							width: '9%',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
							cursor: isImageLoading || !currentImage || currentImage.fileName === images[images.length - 1]?.fileName ? 'default' : 'pointer',
							zIndex: 1000,
							transition: 'background 0.2s ease',
							'&:hover': {
								backgroundImage: isImageLoading || !currentImage || currentImage.fileName === images[images.length - 1]?.fileName ? 'none' : 'linear-gradient(to left, rgba(0,0,0,0.48), transparent)',
								'& .nav-arrow': {
									opacity: isImageLoading || !currentImage || currentImage.fileName === images[images.length - 1]?.fileName ? 0.22 : 1,
									transform: 'scale(1.12)',
								},
								'& .nav-arrow-shell': {
									background: isImageLoading || !currentImage || currentImage.fileName === images[images.length - 1]?.fileName ? 'transparent' : 'rgba(0,0,0,0.30)',
								},
							},
							'&:active': {
								'& .nav-arrow-shell': { background: 'rgba(0,0,0,0.46)' },
							},
						}}
					>
						<span className="nav-arrow-shell">
						<ChevronRightIcon
							className="nav-arrow"
							sx={{
								color: '#fff',
								fontSize: '2rem',
									filter: 'drop-shadow(0 1px 5px rgba(0,0,0,0.8))',
									transition: 'opacity 0.2s ease, transform 0.2s ease',
								}}
							/>
						</span>
					</Box>

					{/* Top-right utility */}
					{currentCoordinate && (
						<div className="viewer__top-right">
						<Tooltip title={showStreetView ? 'Close Street View' : 'Open Street View'} placement="bottom">
							<IconButton
								aria-label="toggle street view"
								size="small"
								onClick={() => setShowStreetView((prev) => !prev)}
								className={`viewer__utility-btn${showStreetView ? ' viewer__utility-btn--active' : ''}`}
							>
								<StreetviewIcon sx={{ fontSize: '1.1rem' }} />
							</IconButton>
						</Tooltip>
					</div>
				)}

				{/* Street View overlay */}
				{showStreetView && currentCoordinate && (
					<div className="viewer__streetview-overlay">
						<div ref={streetViewRef} className="viewer__streetview-pano" />
						<button
							className="viewer__streetview-close"
							aria-label="close street view"
							onClick={() => { panoramaRef.current = null; setShowStreetView(false); }}
						>
							✕
						</button>
					</div>
				)}

				<a-scene embedded ref={sceneRef} vr-mode-ui="enabled: false">
							<a-camera wasd-controls-enabled="false" ref={cameraRef} />
							<a-assets>
								{images.slice(fromIndex, toIndex).map((image) => {
									const key = `${projectId}/images/${image.fileName}`;
									const url = visibleImageUrls[key];
									
									if (!url) return null;

									return (
										<img
											onLoad={() => handleImageLoad(image.fileName)}
											id={`img-${image.fileName}`}
											src={url}
											crossOrigin="anonymous"
											alt="360"
											key={image.fileName}
										/>
									);
								})}
							</a-assets>
							<a-sky
								rotation="5 100 0"
								id="sky"
								src={currentImageLoaded && currentImage?.fileName ? `#img-${currentImage.fileName}` : ''}
								className={currentImageLoaded ? 'loaded' : ''}
							/>
						</a-scene>
						{/* Viewer bottom bar */}
						{images.length > 0 && (
							<div className="viewer__bottom-bar">
								<div className="viewer__bottom-left">
									<Tooltip title="Previous image" placement="top">
										<span>
											<IconButton
												aria-label="previous image"
												size="small"
												disabled={isImageLoading || !currentImage || currentImage.fileName === images[0]?.fileName}
												onClick={prevImage}
												className="viewer__utility-btn"
											>
												<ArrowBackIosIcon sx={{ fontSize: '1rem' }} />
											</IconButton>
										</span>
									</Tooltip>
									<Tooltip title="Next image" placement="top">
										<span>
											<IconButton
												aria-label="next image"
												size="small"
												disabled={isImageLoading || !currentImage || currentImage.fileName === images[images.length - 1]?.fileName}
												onClick={nextImage}
												className="viewer__utility-btn"
											>
												<ArrowForwardIosIcon sx={{ fontSize: '1rem' }} />
											</IconButton>
										</span>
									</Tooltip>
								</div>
								<div className="viewer__bottom-right">
									<Tooltip title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'} placement="top">
										<IconButton
											aria-label={isFullscreen ? 'exit fullscreen' : 'fullscreen'}
											size="small"
											onClick={handleFullscreen}
											className="viewer__utility-btn"
										>
											{isFullscreen
												? <FullscreenExitIcon sx={{ fontSize: '1.1rem' }} />
												: <FullscreenIcon sx={{ fontSize: '1.1rem' }} />
											}
										</IconButton>
									</Tooltip>
								</div>
							</div>
						)}
					</Card>
				</>
			) : (
				<div style={{ height: '100%', width: '100%' }}>
					<img
						src={videoBackgroundDefault}
						alt="blurred background with a street"
						className="video__video-background"
					/>
					<span className="video__button-container">Please Select a Project</span>
				</div>
			)}
		</div>
	);
};

export default MediaDisplay;
